import { Modal, Typography } from "antd"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { Route, Switch } from "react-router-dom"
import { Redirect } from "react-router-dom"
import { useHistory } from "react-router-dom"
import styled from "styled-components"

import FullHeight from "../../_shared/components/FullHeight"
import { useStateValue } from "../../_shared/context/AppStateStore"
import * as api from "../../api"
import CommandLine from "./CommandLine"
import ForgotPassword from "./ForgotPassword"
import LoginForm from "./LoginForm"
import PasswordReset from "./PasswordReset"
import SignupForm from "./SignupForm"

// ----------------------------------------------------------------------------

function _AuthPage(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { className } = props

    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { Title, Text } = Typography

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const [, dispatch] = useStateValue()

    const [errorMessage, setErrorMessage] = useState(false)
    const [isResetRequestSent, setIsPasswordRequestSent] = useState(false)
    const history = useHistory()

    // -------------------------------------
    // Effects
    // -------------------------------------

    // -------------------------------------
    // Component functions
    // -------------------------------------

    function handleLogin(formValues) {
        api.signIn(formValues.email, formValues.password)
            .then((response) => {
                dispatch({
                    type: "SIGN_IN_FULFILLED",
                    payload: response,
                })
            })
            .catch((error) => {
                console.log(error)
                setErrorMessage("Credenziali non valide")
            })
    }

    function handleSignup(formValues) {
        api.signUp({ ...formValues, role: "agency" })
            .then(() => {
                Modal.success({
                    title: "Richiesta di registrazione effettuata.",
                    content:
                        "La tua richiesta di registrazione è stata presa in carico. Riceverai una notifica quando verrà accettata.",
                })
            })
            .catch((error) => {
                console.log(error)
                setErrorMessage("Non siamo riusciti ad elaborare la richiesta")
            })
    }

    async function handleInitResetRequest(email) {
        try {
            await api.createPasswordResetRequest(email)
            setIsPasswordRequestSent(true)
        } catch (e) {
            console.log(e)
            setErrorMessage(
                "Errore durante la richiesta. Sei sicuro di aver inserito correttamente l'indirizzo mail?"
            )
        }
    }

    async function handleChangePassword(password, token) {
        try {
            await api.updatePasswordWithResetRequest(token, password)
            Modal.success({
                title: "Cambio password avvenuto con successo",
                content:
                    "Effettua il login per riprendere a usare i nostri servizi.",
                okText: "Vai al login",
                onOk: () => history.push("/"),
            })
        } catch (e) {
            if (
                e.statusCode === 401 &&
                e?.serverResponse?.error === "TOKEN_EXPIRED"
            ) {
                return setErrorMessage(
                    "Il codice fornito per il recupero della password è scaduto"
                )
            }

            if (
                e.statusCode === 401 &&
                e?.serverResponse?.error === "TOKEN_ALREADY_USED"
            ) {
                return setErrorMessage(
                    "Il codice fornito per il recupero della password è già stato usato"
                )
            }

            if (
                e.statusCode === 404 &&
                e?.serverResponse?.error === "USER_NOT_FOUND"
            ) {
                return setErrorMessage(
                    "Nessun utente trovato per questo codice."
                )
            }

            if (e.statusCode === 404) {
                return setErrorMessage("Questo codice non è valido")
            }

            setErrorMessage("Qualcosa è andato storto")
        }
    }

    function Error() {
        return errorMessage ? (
            <Text className="error">{errorMessage}</Text>
        ) : (
            <></>
        )
    }

    return (
        <FullHeight
            bgImage="/assets/images/bgLogin.jpg"
            className={className + " full-height"}
        >
            <Switch>
                <Route exact path="/">
                    <LoginForm onFinish={handleLogin} Error={<Error />} />
                </Route>
                <Route exact path="/signup">
                    <SignupForm onFinish={handleSignup} Error={<Error />} />
                </Route>
                <Route exact path="/password-reset">
                    <ForgotPassword
                        onSubmit={handleInitResetRequest}
                        Error={<Error />}
                        isRequestSent={isResetRequestSent}
                    />
                </Route>
                <Route path="/password-reset/:token">
                    <PasswordReset onSubmit={handleChangePassword} />
                </Route>
                {/* Redirect all unhandled routes to root */}
                <Route path="/">
                    <Redirect to={"/"} />
                </Route>
            </Switch>
            <CommandLine />
        </FullHeight>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

_AuthPage.propTypes = {
    className: PropTypes.string.isRequired,
}

_AuthPage.defaultProps = {}

// ----------------------------------------------------------------------------

const AuthPage = styled(_AuthPage)`
    & {
        padding: 100px 0px;
        form {
            background-color: ${(props) => props.theme.colors.transparentBg};
            backdrop-filter: drop(4px);
            border-radius: 20px;
            padding: 56px 24px 46px;
            width: calc(100% - 20px);
            max-width: 464px;

            display: flex;
            flex-direction: column;
            h3 {
                font-size: ${(props) => props.theme.font.big.size};
                line-height: ${(props) => props.theme.font.big.height};
                text-align: center;
                margin-bottom: calc(
                    ${(props) => props.theme.spacing.padding} * 2
                );
            }
            .app-input {
                background-color: ${(props) => props.theme.colors.light};
                margin-bottom: ${(props) => props.theme.spacing.padding};
            }
            .primary {
                width: 100%;
                max-width: 100%;
                margin-top: ${(props) => props.theme.spacing.padding};
            }
        }
        .error {
            padding: 0 2px;
            margin-bottom: ${(props) => props.theme.spacing.padding};
            color: ${(props) => props.theme.colors.error};
        }
        small {
            align-self: center;
            display: block;
            margin-top: 15px;
            font-size: 12px;
            font-weight: 600;

            .section-link {
                cursor: pointer;
                text-decoration: underline;
                /* color: #d81159; */
                font-weight: 700;
            }
        }
    }
`
// ----------------------------------------------------------------------------

export default AuthPage
